import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, FileText, Pencil, Check, X, Receipt, Download, PlayCircle, LifeBuoy } from 'lucide-react';
import { formatCurrency } from '../utils/currency';
import { downloadInvoicePDF } from '../utils/invoicePdf';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientsPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [editingClientId, setEditingClientId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', company: '' });
  const [formData, setFormData] = useState({ name: '', company: '' });

  // Postponed invoices state
  const [allInvoices, setAllInvoices] = useState([]);  // all pending+partial across all clients
  const [postponedDialogClient, setPostponedDialogClient] = useState(null); // client object when dialog open
  const [invoiceDeleteId, setInvoiceDeleteId] = useState(null);
  const [invoiceDeleteDialogOpen, setInvoiceDeleteDialogOpen] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, projectsRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/invoices?status=pending,partial`)
      ]);
      setClients(clientsRes.data);
      setProjects(projectsRes.data);
      setAllInvoices(invoicesRes.data);
      setLoading(false);
    } catch {
      toast.error('Failed to load data');
      setLoading(false);
    }
  };

  const refreshInvoices = async () => {
    try {
      const res = await axios.get(`${API}/invoices?status=pending,partial`);
      setAllInvoices(res.data);
    } catch {
      toast.error('Failed to refresh invoices');
    }
  };

  // Count of pending+partial invoices per client
  const getInvoiceCount = (clientId) => allInvoices.filter(i => i.clientId === clientId).length;

  // Get all pending+partial invoices for a single client (sorted: newest first)
  const getClientInvoices = (clientId) =>
    allInvoices
      .filter(i => i.clientId === clientId)
      .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));

  const handleExecuteInvoice = (invoice) => {
    // Build prefill payload for the Add Transaction dialog on FinanceDetail
    const remaining = Math.max(0, (invoice.totalAmount || 0) - (invoice.amountPaid || 0));
    let projectAllocations = [];
    if (invoice.mode === 'milestone' && invoice.milestoneProjectId) {
      // Single project — one allocation row pre-filled with the remaining amount
      projectAllocations = [{
        projectId: invoice.milestoneProjectId,
        projectName: invoice.milestoneProjectName || '',
        percent: 0,
        amount: remaining,
      }];
    } else if (invoice.mode === 'project_list') {
      // Multi-project — distribute remaining proportionally across the invoice's billing rows
      const total = invoice.totalAmount || 1;
      projectAllocations = (invoice.projectBillingData || []).map(p => ({
        projectId: p.projectId,
        projectName: p.projectName,
        percent: p.billingPercent || 0,
        amount: Math.round((p.amount / total) * remaining),
      }));
    }
    const prefill = {
      name: `Invoice ${invoice.invoiceNumber} - ${invoice.clientName || ''}`.trim(),
      type: 'income',
      amount: remaining,
      invoiceId: invoice.id,
      projectAllocations,
    };
    navigate(`/finance?book=${invoice.financeBook}&prefill=${encodeURIComponent(JSON.stringify(prefill))}`);
  };

  const handlePrintPDF = (invoice) => {
    try {
      downloadInvoicePDF({
        invoiceNumber: invoice.invoiceNumber,
        clientName: invoice.clientName,
        clientCompany: invoice.clientCompany,
        mode: invoice.mode,
        milestoneItems: invoice.milestoneItems || [],
        milestoneProjectName: invoice.milestoneProjectName,
        milestonePaidNote: invoice.milestonePaidNote,
        projectBillingData: invoice.projectBillingData || [],
        totalAmount: invoice.totalAmount,
        issueDate: invoice.issueDate,
        dueDate: invoice.dueDate,
        bankAccount: invoice.bankAccount || {},
        notes: invoice.notes,
        signatureFile: invoice.signatureFile,
        signatureDims: invoice.signatureDims,
        signaturePos: invoice.signaturePos,
      });
      toast.success('Invoice PDF downloaded!');
    } catch (e) {
      console.error(e);
      toast.error('Failed to generate PDF');
    }
  };

  const handleDeleteInvoiceClick = (invoiceId) => {
    setInvoiceDeleteId(invoiceId);
    setInvoiceDeleteDialogOpen(true);
  };

  const confirmDeleteInvoice = async () => {
    if (!invoiceDeleteId) return;
    try {
      await axios.delete(`${API}/invoices/${invoiceDeleteId}`);
      toast.success('Invoice deleted');
      await refreshInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete invoice');
    } finally {
      setInvoiceDeleteId(null);
      setInvoiceDeleteDialogOpen(false);
    }
  };

  const getClientStats = (clientId) => {
    const clientProjects = projects.filter(p => p.clientId === clientId);
    const totalFee = clientProjects.reduce((s, p) => s + (p.contractValue || 0), 0);
    const totalPaid = clientProjects.reduce((s, p) => s + (p.initialPaid || 0) + (p.amountPaid || 0), 0);
    return {
      projectCount: clientProjects.length,
      totalFee,
      totalPaid,
      totalUnpaid: totalFee - totalPaid
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.company) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await axios.post(`${API}/clients`, formData);
      toast.success('Client added!');
      setDialogOpen(false);
      setFormData({ name: '', company: '' });
      fetchData();
    } catch {
      toast.error('Failed to add client');
    }
  };

  const startEdit = (client, e) => {
    e.stopPropagation();
    setEditingClientId(client.id);
    setEditForm({ name: client.name, company: client.company });
  };

  const saveEdit = async (e, clientId) => {
    e.stopPropagation();
    try {
      await axios.put(`${API}/clients/${clientId}`, editForm);
      toast.success('Client updated!');
      setEditingClientId(null);
      fetchData();
    } catch {
      toast.error('Failed to update client');
    }
  };

  const cancelEdit = (e) => {
    e.stopPropagation();
    setEditingClientId(null);
  };

  const handleDeleteClick = (id, e) => {
    e.stopPropagation();
    setClientToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!clientToDelete) return;
    try {
      const res = await axios.delete(`${API}/clients/${clientToDelete}`);
      toast.success(res.data?.message || 'Client deleted', {
        description: 'Press Ctrl+Z or use the Undo Delete button to restore.',
      });
      window.dispatchEvent(new CustomEvent('app:trash-updated'));
      setDeleteDialogOpen(false);
      setClientToDelete(null);
      fetchData();
    } catch {
      toast.error('Failed to delete client');
      setDeleteDialogOpen(false);
    }
  };

  const recoverOrphans = async () => {
    try {
      const res = await axios.post(`${API}/clients/recover-orphans`);
      const n = res.data?.recovered || 0;
      if (n === 0) {
        toast.info('No orphaned clients found. All project/invoice references are intact.');
      } else {
        toast.success(`Recovered ${n} client${n > 1 ? 's' : ''}`, {
          description: res.data.clients.map(c => `${c.name}${c.company ? ' - ' + c.company : ''}`).join(', '),
        });
        fetchData();
      }
    } catch {
      toast.error('Recovery failed');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-lg">Loading...</div></div>;
  }

  return (
    <div className="min-h-screen p-6" data-testid="clients-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 relative">
          <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded absolute top-0 right-0">A.4</span>
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Clients</h1>
              <p className="text-slate-600">Manage your clients and generate billing reports</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={recoverOrphans}
                title="Scan projects/invoices for accidentally-deleted clients and rebuild them"
                data-testid="recover-orphans-btn"
              >
                <LifeBuoy className="w-4 h-4 mr-2" /> Recover Deleted Clients
              </Button>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-client-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Client
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add New Client</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Client Name</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="e.g., Urip Pastintoro" data-testid="client-name-input" />
                  </div>
                  <div>
                    <Label>Company</Label>
                    <Input value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} placeholder="e.g., PT Sample Corp" data-testid="client-company-input" />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" data-testid="submit-client-btn">Add Client</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </div>

        <Card className="bg-white shadow-sm">
          <CardHeader><CardTitle>All Clients ({clients.length})</CardTitle></CardHeader>
          <CardContent>
            {clients.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-600 mb-4">No clients yet. Add your first client!</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Projects</TableHead>
                    <TableHead>Total Fee</TableHead>
                    <TableHead>Total Paid</TableHead>
                    <TableHead>Total Unpaid</TableHead>
                    <TableHead>Report</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.map(client => {
                    const stats = getClientStats(client.id);
                    return (
                      <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                        <TableCell className="font-medium">
                          {editingClientId === client.id ? (
                            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                              <Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} className="h-8 w-32" />
                              <Button size="sm" onClick={e => saveEdit(e, client.id)} className="bg-green-600 hover:bg-green-700 h-7 w-7 p-0"><Check className="w-3 h-3" /></Button>
                              <Button size="sm" variant="outline" onClick={cancelEdit} className="h-7 w-7 p-0"><X className="w-3 h-3" /></Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 group">
                              <span>{client.name}</span>
                              <Button size="sm" variant="ghost" onClick={e => startEdit(client, e)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1 h-6">
                                <Pencil className="w-3 h-3 text-slate-600" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {editingClientId === client.id ? (
                            <Input value={editForm.company} onChange={e => setEditForm({...editForm, company: e.target.value})} className="h-8 w-32" onClick={e => e.stopPropagation()} />
                          ) : (
                            client.company
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{stats.projectCount}</TableCell>
                        <TableCell>{formatCurrency(stats.totalFee)}</TableCell>
                        <TableCell className="text-green-600 font-medium">{formatCurrency(stats.totalPaid)}</TableCell>
                        <TableCell className="text-orange-600 font-medium">{formatCurrency(stats.totalUnpaid)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/client-report?clientId=${client.id}`)}
                              data-testid={`client-report-${client.id}`}
                              disabled={stats.projectCount === 0}
                            >
                              <FileText className="w-4 h-4 mr-1" /> Report
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/invoice?clientId=${client.id}`)}
                              data-testid={`client-invoice-${client.id}`}
                              disabled={stats.projectCount === 0}
                            >
                              <FileText className="w-4 h-4 mr-1" /> Invoice
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPostponedDialogClient(client)}
                              data-testid={`client-postponed-${client.id}`}
                              className="relative"
                            >
                              <Receipt className="w-4 h-4 mr-1" /> Postponed
                              {getInvoiceCount(client.id) > 0 && (
                                <span className="ml-1 text-xs font-bold bg-amber-200 text-amber-900 rounded-full px-1.5 py-0.5">
                                  {getInvoiceCount(client.id)}
                                </span>
                              )}
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={e => handleDeleteClick(client.id, e)} data-testid={`delete-client-${client.id}`}>
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={confirmDelete}
          title="Delete Client"
          description="Are you sure you want to delete this client? Projects will not be deleted but will lose the client assignment."
        />

        {/* Postponed Invoices Dialog (per client) */}
        <Dialog open={!!postponedDialogClient} onOpenChange={(o) => { if (!o) setPostponedDialogClient(null); }}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="postponed-invoices-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Postponed Invoices — {postponedDialogClient?.name}
              </DialogTitle>
            </DialogHeader>
            {postponedDialogClient && (() => {
              const list = getClientInvoices(postponedDialogClient.id);
              if (list.length === 0) {
                return (
                  <div className="text-center py-12 text-slate-500">
                    <Receipt className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No postponed invoices for this client.</p>
                    <p className="text-xs mt-1">Generate an invoice from the Invoice Creator to add one here.</p>
                  </div>
                );
              }
              return (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Issued</TableHead>
                      <TableHead>Due</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Paid</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {list.map(inv => {
                      const percent = inv.totalAmount > 0
                        ? Math.round((inv.amountPaid / inv.totalAmount) * 100)
                        : 0;
                      const remaining = Math.max(0, inv.totalAmount - inv.amountPaid);
                      return (
                        <TableRow key={inv.id} data-testid={`postponed-invoice-row-${inv.id}`}>
                          <TableCell className="font-mono text-sm">{inv.invoiceNumber}</TableCell>
                          <TableCell className="text-xs">{inv.issueDate}</TableCell>
                          <TableCell className="text-xs">{inv.dueDate}</TableCell>
                          <TableCell className="font-medium">{formatCurrency(inv.totalAmount)}</TableCell>
                          <TableCell>
                            <div className="text-xs">
                              <div className="font-medium text-green-700">{formatCurrency(inv.amountPaid)}</div>
                              <div className="text-slate-500">Remaining: {formatCurrency(remaining)}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {inv.status === 'partial' ? (
                              <span className="inline-flex items-center gap-1 text-xs font-bold bg-amber-100 text-amber-800 px-2 py-1 rounded">
                                {percent}% Paid
                              </span>
                            ) : (
                              <span className="text-xs font-medium bg-slate-100 text-slate-700 px-2 py-1 rounded">
                                Pending
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePrintPDF(inv)}
                                data-testid={`print-invoice-${inv.id}`}
                                title="Re-download PDF"
                              >
                                <Download className="w-3.5 h-3.5 mr-1" /> Print
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleExecuteInvoice(inv)}
                                className="bg-green-600 hover:bg-green-700"
                                data-testid={`execute-invoice-${inv.id}`}
                                title="Record payment for this invoice"
                              >
                                <PlayCircle className="w-3.5 h-3.5 mr-1" /> Execute
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteInvoiceClick(inv.id)}
                                disabled={inv.status === 'partial'}
                                data-testid={`delete-invoice-${inv.id}`}
                                title={inv.status === 'partial' ? 'Cannot delete — has payments' : 'Delete invoice'}
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-600" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              );
            })()}
          </DialogContent>
        </Dialog>

        <DeleteConfirmDialog
          open={invoiceDeleteDialogOpen}
          onOpenChange={setInvoiceDeleteDialogOpen}
          onConfirm={confirmDeleteInvoice}
          title="Delete Postponed Invoice"
          description="Are you sure you want to delete this postponed invoice? This cannot be undone."
        />
      </div>
    </div>
  );
};

export default ClientsPage;
